import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { buildWsUrl } from "../utils/ws";
import { isNativeApp } from "../native/runtime";
import { useChatUnread } from "./ChatUnreadContext.jsx";
import PlatformNotificationToast from "../components/common/PlatformNotificationToast.jsx";
import { CHAT_INBOX_CONNECTION_EVENT } from "../constants/chatPolling";

const ChatRealtimeContext = createContext(null);
const MAX_REMEMBERED_MESSAGE_IDS = 500;
const PRESENCE_HEARTBEAT_MS = 25_000;

function chatPath(chat) {
  const id = Number(chat?.id || 0);
  if (!id) return null;

  switch (String(chat?.type || "").toLowerCase()) {
    case "event": {
      const eventId = Number(chat?.event_id || 0);
      return eventId ? `/menu/chats/event/${eventId}` : null;
    }
    case "community":
      return `/menu/chats/group/${id}`;
    case "support":
      return `/menu/chats/support/${id}`;
    case "direct":
      return `/menu/chats/direct/id/${id}`;
    default:
      return null;
  }
}

function isActiveChatPath(pathname, chat) {
  const target = chatPath(chat);
  return Boolean(target && String(pathname || "").startsWith(target));
}

function rememberMessageId(ids, messageId) {
  if (!messageId || ids.has(messageId)) return false;
  ids.add(messageId);
  if (ids.size > MAX_REMEMBERED_MESSAGE_IDS) {
    ids.delete(ids.values().next().value);
  }
  return true;
}

export function ChatRealtimeProvider({ children }) {
  const location = useLocation();
  const { refresh } = useChatUnread();
  const [latestNotification, setLatestNotification] = useState(null);
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const appActiveRef = useRef(
    typeof document === "undefined" || document.visibilityState !== "hidden"
  );
  const pathnameRef = useRef(location.pathname);
  const refreshUnreadRef = useRef(refresh);
  const notifiedMessageIdsRef = useRef(new Set());
  const clearLatestNotification = useCallback(() => setLatestNotification(null), []);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    refreshUnreadRef.current = refresh;
  }, [refresh]);

  const handleInboxEvent = useCallback((payload) => {
    if (String(payload?.event || "").toLowerCase() !== "inbox.message.created") return;

    refreshUnreadRef.current?.();
    window.dispatchEvent(new CustomEvent("vg:chat-inbox-updated", { detail: payload }));

    const chat = payload?.chat || null;
    const message = payload?.message || null;
    const messageId = Number(message?.id || 0);
    if (
      !payload?.show_toast ||
      !chat ||
      !messageId ||
      isActiveChatPath(pathnameRef.current, chat) ||
      !rememberMessageId(notifiedMessageIdsRef.current, messageId)
    ) {
      return;
    }

    setLatestNotification({ chat, message });
  }, []);

  useEffect(() => {
    let disposed = false;
    const nativeRuntime = isNativeApp();
    let inboxConnected = false;

    const publishConnectionState = (connected) => {
      const nextConnected = Boolean(connected);
      if (inboxConnected === nextConnected) return;
      inboxConnected = nextConnected;
      window.dispatchEvent(new CustomEvent(CHAT_INBOX_CONNECTION_EVENT, {
        detail: { connected: nextConnected },
      }));
    };

    const clearReconnect = () => {
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    };

    const clearHeartbeat = () => {
      if (heartbeatTimerRef.current) window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    };

    const sendHeartbeat = () => {
      const socket = socketRef.current;
      if (!appActiveRef.current || !socket || socket.readyState !== WebSocket.OPEN) return;
      try {
        socket.send(JSON.stringify({ action: "presence.heartbeat" }));
      } catch {
        // The socket error/close handlers own reconnection.
      }
    };

    const closeSocket = () => {
      clearHeartbeat();
      publishConnectionState(false);
      const socket = socketRef.current;
      socketRef.current = null;
      if (!socket) return;
      try {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
      } catch {
        // Ignore cleanup errors.
      }
    };

    const scheduleReconnect = () => {
      if (disposed || !appActiveRef.current || reconnectTimerRef.current) return;
      const attempt = reconnectAttemptRef.current || 0;
      const delayMs = Math.min(20_000, 1_000 * 2 ** attempt);
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delayMs);
      reconnectAttemptRef.current = attempt + 1;
    };

    const connect = () => {
      if (disposed || !appActiveRef.current) return;
      clearReconnect();
      closeSocket();

      let socket;
      try {
        socket = new WebSocket(buildWsUrl("/ws/chats/inbox/"));
      } catch {
        scheduleReconnect();
        return;
      }

      socketRef.current = socket;
      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        publishConnectionState(true);
        sendHeartbeat();
        clearHeartbeat();
        heartbeatTimerRef.current = window.setInterval(
          sendHeartbeat,
          PRESENCE_HEARTBEAT_MS
        );
      };
      socket.onmessage = (event) => {
        try {
          handleInboxEvent(JSON.parse(event?.data || "{}"));
        } catch {
          // Ignore malformed websocket payloads.
        }
      };
      socket.onerror = () => {
        try {
          socket.close();
        } catch {
          // Ignore socket errors; onclose schedules the retry.
        }
      };
      socket.onclose = () => {
        clearHeartbeat();
        publishConnectionState(false);
        if (!disposed) scheduleReconnect();
      };
    };

    const setAppActive = (active) => {
      const nextActive = Boolean(active);
      if (appActiveRef.current === nextActive) return;
      appActiveRef.current = nextActive;
      if (nextActive) {
        reconnectAttemptRef.current = 0;
        connect();
      } else {
        clearReconnect();
        closeSocket();
      }
    };

    const handleNativeAppState = (event) => {
      setAppActive(event?.detail?.isActive !== false);
    };
    const handleVisibilityChange = () => {
      if (nativeRuntime) return;
      setAppActive(document.visibilityState !== "hidden");
    };

    window.addEventListener("vg:native-app-state", handleNativeAppState);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    connect();
    return () => {
      disposed = true;
      clearReconnect();
      clearHeartbeat();
      closeSocket();
      window.removeEventListener("vg:native-app-state", handleNativeAppState);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleInboxEvent]);

  const value = useMemo(
    () => ({
      latestNotification,
      clearLatestNotification,
    }),
    [clearLatestNotification, latestNotification]
  );

  return <ChatRealtimeContext.Provider value={value}>{children}</ChatRealtimeContext.Provider>;
}

function useChatRealtime() {
  return useContext(ChatRealtimeContext) || {
    latestNotification: null,
    clearLatestNotification: () => {},
  };
}

function notificationBadge(chat, t) {
  const type = String(chat?.type || "").toLowerCase();
  if (type === "direct") return t("chats.direct_chat.title", { defaultValue: "Direct chat" });
  if (type === "support") return t("chats.support.title", { defaultValue: "Support" });
  return String(chat?.label || "").trim() || t(
    type === "event" ? "chats.tabs.events" : "chats.tabs.groups",
    { defaultValue: type === "event" ? "Event chat" : "Group chat" }
  );
}

export function ChatMessageNotificationToast() {
  const { latestNotification, clearLatestNotification } = useChatRealtime();
  const navigate = useNavigate();
  const { t } = useTranslation("menu");
  const portalRoot =
    typeof document !== "undefined"
      ? document.getElementById("modal-root") || document.body
      : null;

  useEffect(() => {
    if (!latestNotification) return undefined;
    const timerId = window.setTimeout(clearLatestNotification, 10_000);
    return () => window.clearTimeout(timerId);
  }, [clearLatestNotification, latestNotification]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") clearLatestNotification();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [clearLatestNotification]);

  if (!latestNotification) return null;

  const { chat, message } = latestNotification;
  const messageText = String(message?.text || "").replace(/\s+/g, " ").trim();
  const hasAttachment = Array.isArray(message?.attachments) && message.attachments.length > 0;
  const preview = messageText || (hasAttachment
    ? t("chats.list.photo_preview", { defaultValue: "Photo" })
    : t("chats.list.no_preview", { defaultValue: "New message" }));
  const sender = String(message?.author_code || message?.author_username || "").trim() ||
    t("chats.support.title", { defaultValue: "Support" });

  return (
    <PlatformNotificationToast
      badge={notificationBadge(chat, t)}
      title={sender}
      message={preview}
      openAriaLabel={t("chats.list.open_notification", { defaultValue: "Open chat" })}
      closeAriaLabel={t("chats.header.close_aria", { defaultValue: "Close" })}
      onDismiss={clearLatestNotification}
      onOpen={() => {
        const target = chatPath(chat);
        clearLatestNotification();
        if (target) navigate(target);
      }}
      portalRoot={portalRoot}
    />
  );
}
